import lodashPick from 'lodash-es/pick';
import isEmail from 'validator/es/lib/isEmail';

import {
    type ApiGetContactSaleByLanguageParams,
    type ContactSaleEntity,
    getApiGetContactSaleByLanguageQueryKey,
    useApiGetContactSaleByLanguage,
} from '~/apiClient';
import type { PlatformSaleItemEntity } from '~/types/sale.type';

export type SalesContact = {
    fullName: string;
    email: string;
    phone: string;
    whatsAppLink: string;
    initialFormMessage: string;
    supportedLanguages?: string[];
    profileImage?: string | null;
    sale?: PlatformSaleItemEntity;
    appliedFilters?: AppliedFilter[];
};

export interface UseSalesContactProps {
    sale?: MaybeRefOrGetter<PlatformSaleItemEntity>;
    appliedFilters?: MaybeRefOrGetter<AppliedFilter[]>;
}

type UseSalesContactReturn = {
    salesContact: ComputedRef<SalesContact | undefined>;
    suspense: () => Promise<unknown>;
};

const PREFERRED_SALES_CONTACT_COOKIE_NAME = 'flq_preferred_sales_contact';

export const useSalesContact = ({ sale, appliedFilters }: UseSalesContactProps): UseSalesContactReturn => {
    const { isLoggedIn, data: authData } = useAuthUtils();
    const route = useRoute();
    const config = useRuntimeConfig();
    const { tt } = useTypedI18n();
    const { locale } = useI18n();
    const preferredSalesContactCookie = useCookie(PREFERRED_SALES_CONTACT_COOKIE_NAME, { maxAge: 24 * 3600 * 365 });

    const formatContactSaleParams = (): ApiGetContactSaleByLanguageParams => {
        const cookieValue = preferredSalesContactCookie.value;
        const previousContactSaleEmail = cookieValue && isEmail(cookieValue) ? cookieValue : undefined;

        return { language: locale.value, previousContactSaleEmail };
    };

    const contactSaleParams = ref(formatContactSaleParams());

    const { data: getContactSaleData, suspense: getContactSaleSuspense } = useApiGetContactSaleByLanguage(contactSaleParams, {
        query: {
            enabled: () => !isLoggedIn.value,
            queryKey: computed(() => getApiGetContactSaleByLanguageQueryKey(contactSaleParams.value)),
        },
    });

    const salesContact = computed<SalesContact | undefined>(() => {
        const _sale = toValue(sale);
        const _appliedFilters = toValue(appliedFilters);

        const salesContactEntity: ContactSaleEntity | undefined = authData.value?.user?.company?.salesRep ?? getContactSaleData.value ?? undefined;

        if (!salesContactEntity) return;

        let wappMsgTitle = tt('salesContact.card.whatsAppLinkMessage');
        let wappMsgBody = '';
        let initialFormMessage = '';
        const pageUrl = `${config.public.frontendBaseUrl}${route.fullPath}`;

        if (_sale) {
            const { title: saleTitle } = useSaleTitle(_sale);
            wappMsgTitle = `${_sale.mainAsset.brand.brandValue} ${_sale.mainAsset.model.modelValue} - ${_sale.incrementId}`;
            wappMsgBody = tt('salesContact.card.whatsAppLinkMessage');
            initialFormMessage = tt('salesContact.contactForm.messageSalePrefill', { title: saleTitle.value });
        } else if (_appliedFilters) {
            wappMsgTitle = tt('salesContact.card.whatsAppLinkSlpTitle');
            wappMsgBody = _appliedFilters.map(({ label }) => label).join(', ');
            initialFormMessage = tt('salesContact.contactForm.messageAppliedFilterPrefill');
        }

        const whatsAppMessage = `${wappMsgTitle}\n${pageUrl}\n\n${wappMsgBody}`;
        const whatsAppLink = `https://wa.me/${salesContactEntity.whatsappNumber.replace(/^\+/, '').replace(/\s/g, '')}?text=${encodeURIComponent(whatsAppMessage)}`;

        return {
            ...lodashPick(salesContactEntity, ['email', 'phone', 'supportedLanguages', 'profileImage']),
            fullName: `${salesContactEntity.firstname} ${salesContactEntity.lastname}`,
            whatsAppLink,
            initialFormMessage,
            sale: _sale,
            appliedFilters: _appliedFilters,
        };
    });

    const suspense = isLoggedIn.value ? (): Promise<void> => Promise.resolve() : getContactSaleSuspense;

    watch(getContactSaleData, () => {
        if (!isLoggedIn.value && getContactSaleData.value) {
            preferredSalesContactCookie.value = getContactSaleData.value.email;
        }
    });

    watch(locale, () => {
        contactSaleParams.value = formatContactSaleParams();
    });

    return { salesContact, suspense };
};
